import type { FC } from 'react'
import React, { useMemo, memo } from 'react'
import { Line } from '@nivo/line'
import AutoSizer from 'react-virtualized-auto-sizer'
import { isEngageUser, useGetReviewsCountHistoryQuery } from '@homeviews/store'
import { Spinner } from '@homeviews/shared-ui'
import { getChartTooltipDate, isEqual } from '@homeviews/utils'
import NoData from '../NoData'
import { transformReviewsCountHistoryData } from '../../helpers/transform'
import ChartLegend from '../ChartLegend'
import { CHART_BRAND_COLORS } from '../../helpers/settings'
import { chartTheme } from '../../helpers/layout'
import ChartTooltip from '../ChartTooltip'
import { useSelector } from 'react-redux'
interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
}

const ReviewsHistoryChart: FC<IProps> = memo(
  ({ activeFilters, graphDisabled, gridRef }) => {
    const isEngagePackage = Boolean(useSelector(isEngageUser))
    const { data: reviewsCountHistoryData, isFetching } =
      useGetReviewsCountHistoryQuery(
        {
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled,
        },
      )

    const transformedData: any = useMemo(
      () => transformReviewsCountHistoryData(reviewsCountHistoryData?.data),
      [reviewsCountHistoryData?.data],
    )

    const data = transformedData.data
    const maxChartValue = transformedData.maxChartValue

    if (isFetching) {
      return <Spinner centered />
    }

    if (!data || !data?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
        return <NoData />
      }
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    return (
      <div className="l-chartContainer">
        <div className="l-chartWrapper">
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Line
                  data={data}
                  theme={chartTheme}
                  colors={{ datum: 'color' }}
                  enableGridX={false}
                  height={height}
                  width={width}
                  xScale={{
                    type: 'time',
                    format: '%Y-%m-%d',
                  }}
                  xFormat="time:%Y-%m-%d"
                  yScale={{
                    type: 'linear',
                    stacked: false,
                    max: maxChartValue,
                  }}
                  axisLeft={{
                    legendOffset: 12,
                    tickSize: 0,
                    tickPadding: 10,
                  }}
                  axisBottom={{
                    format: '%d/%m/%y',
                    legendOffset: -12,
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    tickValues: Math.floor(width / 90),
                  }}
                  pointColor={'white'}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  margin={{ top: 50, right: 30, bottom: 70, left: 40 }}
                  pointSize={7}
                  useMesh={true}
                  tooltip={(tooltip) => {
                    return (
                      <div>
                        <ChartTooltip>
                          <ChartLegend
                            direction="vertical"
                            data={[
                              {
                                text: `${getChartTooltipDate(
                                  tooltip.point?.data?.x,
                                )}: ${tooltip.point?.data?.yFormatted}`,
                                shapeColor: tooltip?.point?.serieColor,
                              },
                            ]}
                          />
                        </ChartTooltip>
                      </div>
                    )
                  }}
                />
              )
            }}
          </AutoSizer>
        </div>
        <ChartLegend
          data={[
            {
              text: 'Review count',
              shapeColor: CHART_BRAND_COLORS.green1,
            },
          ]}
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default ReviewsHistoryChart
